import { queryClient } from '@/api';
import CloseIcon from '@/assets/w-close.svg';
import { HostConfigurationQueryKey } from '@/infrastructure/widgetHooks/useWidgetHostConfiguration';
import HostDetails from '@/types/hostDetails';
import {
  ActionIcon,
  Drawer,
  Paper,
  Stack,
  Text
} from '@mantine/core';
import clsx from 'clsx';
import { FC, useContext, useState } from 'react';

import { useFocus } from '@features/Chat/ChatInputContext.tsx';

import PlatformLogo from '@assets/travelonelogo.svg';

import CurrencyPicker from '../CurrencyPicker';
import LanguagePicker from '../LanguagePicker';
import { useConsent } from '@/infrastructure/consent/consentManager';
import useIntl from '@/infrastructure/hooks/useIntl';
import { ModalContext } from '../WidgetModal';
import MenuIcon from './assets/menu.svg';
import PrivacyShieldIcon from './assets/privacy-shield.svg';
import styles from './index.module.css';

interface IProps {}

const Header: FC<IProps> = ({}) => {
  const tConsent = useIntl('consent');
  const { onModalClose } = useContext(ModalContext);
  const { openConsentSettings } = useConsent();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const hostDetails = queryClient.getQueryData<{ hostDetails: HostDetails }>(
    HostConfigurationQueryKey
  )?.hostDetails;
  const { focusInput } = useFocus();

  const handleDrawerClose = () => {
    setIsMobileMenuOpen(false);
    // Wait for drawer animation to complete before focusing
    setTimeout(() => {
      focusInput();
    }, 300); // Adjust timeout based on drawer's transition duration
  };

  const logo = hostDetails?.logoUrl;
  const hasCloseButton = !window.TravelOneConfig.containerId;

  return (
    <div className={clsx(styles['header-wrapper'])}>
      <ActionIcon
        className={styles.menuButton}
        variant="outline-invert"
        radius="md"
        size="lg"
        fw={400}
        c="white"
        fz="sm"
        tabIndex={-1}
        onClick={() =>
          setIsMobileMenuOpen(isMobileMenuOpen => !isMobileMenuOpen)
        }
      >
        <MenuIcon />
      </ActionIcon>
      <Stack gap="0" className={styles.headerContent}>
        <div className={clsx(styles['header-left'])}>
          <PlatformLogo />
          {logo && (
            <>
              <div className={styles.separator}></div>
              <img src={logo} />
            </>
          )}
        </div>
        <Text size="sm" c="d1gray.4">
          Official {hostDetails?.name} travel partner
        </Text>
      </Stack>
      <div className={clsx(styles['header-right'])}>
        <div className={styles.languagePickerContainer}>
          <CurrencyPicker />
        </div>
        <div className={styles.languagePickerContainer}>
          <LanguagePicker />
        </div>
        <div className={styles.languagePickerContainer}>
          <ActionIcon
            variant="outline-invert"
            radius="md"
            size="lg"
            fw={400}
            c="white"
            fz="sm"
            tabIndex={-1}
            title={tConsent('privacyPreferences')}
            aria-label={tConsent('privacyPreferences')}
            onClick={openConsentSettings}
          >
            <PrivacyShieldIcon />
          </ActionIcon>
        </div>

        {hasCloseButton && (
          <ActionIcon
            variant="outline-invert"
            radius="md"
            size="lg"
            fw={400}
            c="white"
            fz="sm"
            tabIndex={-1}
            onClick={onModalClose}
          >
            <CloseIcon />
          </ActionIcon>
        )}
      </div>
      <Drawer.Root
        size="80%"
        opened={isMobileMenuOpen}
        bg="primary.9"
        onClose={handleDrawerClose}
        transitionProps={{ duration: 300 }}
      >
        <Drawer.Overlay />
        <Drawer.Content bg="primary.9">
          <Drawer.Header bg="primary.9">
            <div className={clsx(styles['header-left'])}>
              <PlatformLogo />
              {logo && (
                <>
                  <div className={styles.separator}></div>
                  <img src={logo} />
                </>
              )}
            </div>
          </Drawer.Header>
          <Drawer.Body>
            <Stack>
              <LanguagePicker withName onChange={handleDrawerClose} />
              <CurrencyPicker withName fullWidth onChange={handleDrawerClose} />
              <Paper
                p="xs"
                bg="primary.8"
                style={{ cursor: 'pointer', borderRadius: '8px' }}
                onClick={() => {
                  handleDrawerClose();
                  openConsentSettings();
                }}
              >
                <Text size="sm" c="white" fw={500}>
                  🛡️ {tConsent('privacyPreferences')}
                </Text>
              </Paper>
            </Stack>
          </Drawer.Body>
        </Drawer.Content>
      </Drawer.Root>
    </div>
  );
};

export default Header;
